import safeJsonParse from '../utils/jsonSafe';
import ErrorHandler from '../utils/errorHandler';
import Logger from '../utils/logger';

// Determine API host: use environment variable, or same hostname as frontend, or default to localhost
const getApiHost = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    // If full URL is provided, use it directly
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // Default backend port - can be overridden via REACT_APP_API_PORT
  const API_PORT = process.env.REACT_APP_API_PORT || '8006';
  
  if (typeof window !== 'undefined') {
    // Electron desktop app loads from file:// — use hosted backend (nginx proxies /api/)
    if (window.location?.protocol === 'file:' || window.electronAPI !== undefined) {
      return 'https://chipdesigner.ai';
    }
    
    if (window.location?.hostname) {
      const hostname = window.location.hostname;
      const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
      
      // For chipdesigner domains, use same origin (nginx proxies /api/ to backend)
      if (hostname === 'chipdesigner.ai' || hostname.endsWith('.chipdesigner.ai')) {
        return `${protocol}://${hostname}`;
      }
      
      return `${protocol}://${hostname}:${API_PORT}`;
    }
  }
  
  return `http://localhost:${API_PORT}`;
};

const API_BASE_URL = getApiHost();

export class BaseApiService {
  constructor() {
    this.baseUrl = `${API_BASE_URL}/api`;
    this.isSecureContext =
      typeof window !== 'undefined' && window.location?.protocol === 'https:';
    
    // Make isElectron a getter that re-checks dynamically
    Object.defineProperty(this, 'isElectron', {
      get: function() {
        return typeof window !== 'undefined' && (
          window.location?.protocol === 'file:' || 
          window.electronAPI !== undefined ||
          (window.process?.type === 'renderer') ||
          (window.navigator?.userAgent?.includes('Electron'))
        );
      }
    });
    
    // Make useIPC a getter that re-checks each time
    Object.defineProperty(this, 'useIPC', {
      get: function() {
        return this.isElectron && window.electronAPI?.backend !== undefined;
      }
    });
    
    Logger.debug('BaseApiService initialized', { isElectron: this.isElectron, useIPC: this.useIPC });
    if (this.useIPC) {
      Logger.debug('IPC backend available', { methods: Object.keys(window.electronAPI.backend || {}) });
    } else if (this.isElectron) {
      Logger.warn('IPC backend not available. Using HTTP fallback.');
      Logger.debug('window.electronAPI presence', { exists: !!window.electronAPI });
      Logger.debug('window.electronAPI.backend presence', { exists: !!window.electronAPI?.backend });
    }
    
    // Wait for window to fully load before checking IPC (for HTTP URLs)
    if (typeof window !== 'undefined') {
      if (document.readyState === 'complete') {
        setTimeout(() => {
          this.testElectronAPI();
          this.checkIPCAvailability();
        }, 100);
      } else {
        window.addEventListener('load', () => {
          setTimeout(() => {
            this.testElectronAPI();
            this.checkIPCAvailability();
          }, 100);
        });
      }
    }
  }
  
  // Test function to check electronAPI availability
  testElectronAPI() {
    Logger.debug('Testing electronAPI availability', {
      windowExists: typeof window !== 'undefined',
      electronAPIExists: typeof window !== 'undefined' && typeof window.electronAPI !== 'undefined',
      electronAPIKeys: typeof window !== 'undefined' && window.electronAPI ? Object.keys(window.electronAPI) : null,
      backendExists: typeof window !== 'undefined' && window.electronAPI?.backend !== 'undefined',
      backendKeys: typeof window !== 'undefined' && window.electronAPI?.backend ? Object.keys(window.electronAPI.backend) : null
    });
  }
  
  // Method to check IPC availability dynamically
  checkIPCAvailability() {
    const wasElectron = this.isElectron;
    const wasIPC = this.useIPC;
    
    // Re-check IPC (isElectron getter will re-check automatically)
    const nowHasIPC = this.isElectron && window.electronAPI?.backend !== undefined;
    
    if (!wasIPC && nowHasIPC) {
      Logger.debug('IPC backend became available', { methods: Object.keys(window.electronAPI.backend || {}) });
    } else if (wasIPC && !nowHasIPC) {
      Logger.warn('IPC backend became unavailable');
    }
    
    return nowHasIPC;
  }

  // Get session token from secure storage (Electron only)
  // For web app, cookies are used automatically (httpOnly cookies set by backend)
  getSessionToken() {
    // Check for electronAPI directly (more reliable than isElectron getter)
    const isElectronEnv = typeof window !== 'undefined' && (
      window.electronAPI !== undefined ||
      window.location?.protocol === 'file:'
    );
    
    // For web app, return null - cookies are handled automatically by browser
    if (!isElectronEnv) {
      return Promise.resolve(null);
    }
    
    // For Electron, use secure electron-store via IPC
    if (window.electronAPI?.getSessionToken) {
      return window.electronAPI.getSessionToken().catch((e) => {
        Logger.warn('Failed to get session token from secure storage', { error: e, component: 'ApiService' });
        return null;
      });
    }
    
    // No fallback - secure storage is required for Electron
    return Promise.resolve(null);
  }

  // Get user ID from localStorage or return default
  getUserId() {
    try {
      // Try to get from localStorage (Electron)
      if (this.isElectron) {
        const userId = localStorage.getItem('user_id');
        if (userId) return userId;
      }
      // Try to get from session storage
      if (typeof window !== 'undefined' && window.sessionStorage) {
        const userId = sessionStorage.getItem('user_id');
        if (userId) return userId;
      }
    } catch (e) {
      // Ignore errors
    }
    return null; // Will default to 'default_user' in calling code
  }

  // Set session token in secure storage (Electron only)
  // For web app, cookies are set by backend automatically (httpOnly cookies)
  setSessionToken(token) {
    // For web app, tokens are stored in httpOnly cookies by backend - no action needed
    if (!this.isElectron) {
      return Promise.resolve();
    }
    
    // For Electron, use secure electron-store via IPC
    if (window.electronAPI?.setSessionToken) {
      return window.electronAPI.setSessionToken(token).catch((e) => {
        const handled = ErrorHandler.handle(e, {
          component: 'ApiService',
          action: 'setSessionToken',
          metadata: { storage: 'electron-store' }
        });
        Logger.error('Failed to save session token to secure storage', { handled, component: 'ApiService' });
        return Promise.resolve(); // Don't throw - just log the error
      });
    }
    
    // No fallback - secure storage is required for Electron
    Logger.warn('electronAPI.setSessionToken not available', { component: 'ApiService' });
    return Promise.resolve();
  }

  // Get fetch headers with token (for Electron)
  getFetchHeaders(additionalHeaders = {}) {
    const headers = { ...additionalHeaders };
    if (this.isElectron) {
      headers['X-Client-Type'] = 'electron';  // Always send this to identify Electron
      // Token will be added in request() method where we can await it
    }
    return headers;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    Logger.debug('Making API request', { url, endpoint, component: 'ApiService' });
    
    const headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    };

    // For Electron, send token from secure storage as header (cookies don't work with file://)
    // Also send a header to identify Electron requests
    if (this.isElectron) {
      headers['X-Client-Type'] = 'electron';  // Always send this to identify Electron
      // Get token asynchronously
      const token = await this.getSessionToken();
      if (token) {
        headers['X-Session-Token'] = token;
      }
    }
    
    const config = {
      method: options.method || 'GET',
      credentials: this.isElectron ? 'omit' : 'include', // Don't send cookies in Electron
      ...options,
      headers,
    };

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);
      Logger.debug('API response', { status: response.status, endpoint, method: options.method || 'GET' });
      
      // Handle 401 by clearing invalid token (Electron only)
      if (response.status === 401 && this.isElectron) {
        await this.setSessionToken(null);
      }
      
      if (!response.ok) {
        // Create error with response data for better error handling
        let errorData = null;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            errorData = await response.json();
          } else {
            errorData = { message: await response.text() };
          }
        } catch (e) {
          errorData = { message: response.statusText };
        }
        
        const apiError = new Error(errorData?.message || `API Error: ${response.status} ${response.statusText}`);
        apiError.response = response;
        apiError.data = errorData;
        throw apiError;
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const result = await response.json();
        
        // Auto-save token if present in response (for Electron)
        if (this.isElectron && result.session_token) {
          await this.setSessionToken(result.session_token);
        }
        
        return result;
      }
      
      return await response.text();
    } catch (error) {
      // Use ErrorHandler for consistent error handling
      const handled = ErrorHandler.handle(error, {
        component: 'ApiService',
        action: 'request',
        endpoint: endpoint,
        metadata: {
          method: options.method || 'GET',
          url: url
        }
      });
      Logger.error('API request failed', { handled });
      // Re-throw with handled error information
      const enhancedError = new Error(handled.message);
      enhancedError.code = handled.code;
      enhancedError.category = handled.category;
      enhancedError.recoverable = handled.recoverable;
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  async setSessionCookie(token, { maxAgeSeconds = 7 * 24 * 60 * 60 } = {}) {
    if (!token) {
      // Clear cookie/token
      await this.clearSessionCookie();
      return;
    }
    
    // For Electron, use secure storage (cookies don't work with file://)
    if (this.isElectron) {
      await this.setSessionToken(token);
      return;
    }
    
    // For web, backend sets httpOnly cookies automatically
    // We only set a client-side cookie as a fallback for compatibility
    // Note: The backend sets httpOnly cookies which are more secure
    const sameSite = this.isSecureContext ? 'None' : 'Lax';
    let cookie = `session_token=${token}; path=/; max-age=${maxAgeSeconds}; SameSite=${sameSite}`;
    if (this.isSecureContext) {
      cookie += '; Secure';
    }
    document.cookie = cookie;
  }

  async clearSessionCookie() {
    // For Electron, clear secure storage
    if (this.isElectron) {
      await this.setSessionToken(null);
      return;
    }
    
    // For web, clear cookie
    const sameSite = this.isSecureContext ? 'None' : 'Lax';
    let cookie = `session_token=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=${sameSite}`;
    if (this.isSecureContext) {
      cookie += '; Secure';
    }
    document.cookie = cookie;
  }
}

